import type * as React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { buildRowCount } from "@/useCases/rowCount";
import { CheckCircle } from "lucide-react";
import { type ColumnDef } from "@tanstack/react-table";
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";

import { TAsyncState } from "@/store";
import { Card } from "@/ui/atoms";
import { DataTable } from "@/ui/molecules";
import { STRING_MULTI_SELECT_BLANK } from "@/utils/tableMultiSelectFilter";
import { useServerTable } from "@/useCases/useServerTable";
import type { TApiLineItemQuery } from "@/apis/proda_finance/types";
import {
    lineItemColumnFiltersToQuery,
    hasAnyLineItemColumnFilter,
    type TLineItemFacets,
} from "./lineItemQuery";

// Constants for display values
const REBALANCE_ACCOUNT_NAME = "Cash - Internal / Intracompany Transfer";

const getDisplayRef = (ref: string | null): string => {
    if (ref === "GL_MOVEMENT") return "GL Generated Movement";
    if (ref === "REBALANCE") return "Rebalance";
    return ref ?? "—";
};

const getDisplaySourceAccountName = (ref: string | null, accountName: string | null): string => {
    if (ref === "REBALANCE") return REBALANCE_ACCOUNT_NAME;
    return accountName ?? "—";
};

// Types for GL lines view (matches TApiFileLineItem property names after camelCase conversion)
export type TGLLineItem = {
    id: string;
    lineNumber: number;
    trannum: number | null;
    accountCode: string;
    accountName: string | null;
    mappedAccountCode: string | null;
    propertyCode: string;
    mappedPropertyCode: string | null;
    asset: string | null;
    mappedAsset: string | null;
    investment: string | null;
    mappedInvestment: string | null;
    sourceSystem: string | null;
    amount: string | null;
    debit: string | null;
    credit: string | null;
    transactionDate: string | null;
    postingDate: string | null;
    bookNumber: number;
    description: string | null;
    reference: string | null;
    isGLGeneratedLine: boolean;
};

const EMPTY_GL_LINES: TGLLineItem[] = [];

const BLANK_FILTER_OPTION = { value: STRING_MULTI_SELECT_BLANK, label: "(Blanks)" };

const toOptions = (values: string[]): { value: string; label: string }[] =>
    values.map((v) => ({ value: v, label: v }));

/** Build the GL Lines columns in server mode: filter types come from the
    cardinality policy; multiSelect option lists come from the facets endpoint;
    no client filterFn (the server filters the page). */
function createGLLinesColumns(facets: TLineItemFacets | null): ColumnDef<TGLLineItem>[] {
    const f = facets;
    const numberRange = { filterType: "numberRange" as const };
    const text = { filterType: "text" as const };
    const date = { filterType: "date" as const };
    const multi = (values: string[]) => ({
        filterType: "multiSelect" as const,
        filterOptions: toOptions(values),
    });

    // Same as `multi`, plus a pinned "(Blanks)" option: asset is legitimately blank
    // on entity-level lines, and reviewers need to isolate them.
    const multiWithBlank = (values: string[]) => ({
        filterType: "multiSelect" as const,
        filterOptions: [BLANK_FILTER_OPTION, ...toOptions(values)],
    });

    return [
        {
            accessorKey: "isValid",
            header: "Is Valid",
            enableColumnFilter: false,
            enableSorting: false,
            meta: { hidePerColumnTextSearch: true },
            cell: () => <CheckCircle className="h-4 w-4 text-green-600" />,
        },
        {
            accessorKey: "lineNumber",
            header: "Row Number",
            cell: ({ row }) => <span className="font-mono text-xs">{row.original.lineNumber}</span>,
            meta: numberRange,
        },
        {
            accessorKey: "trannum",
            header: "Trannum",
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.trannum ?? "—"}</span>
            ),
            meta: numberRange,
        },
        {
            accessorKey: "mappedPropertyCode",
            header: "Mapped Entity",
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.mappedPropertyCode || "—"}</span>
            ),
            meta: multi(f?.mapped_property_codes ?? []),
        },
        {
            accessorKey: "propertyCode",
            header: "Entity",
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.propertyCode || "—"}</span>
            ),
            meta: multi(f?.property_codes ?? []),
        },
        {
            accessorKey: "asset",
            header: "Asset",
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.asset || "—"}</span>
            ),
            meta: multiWithBlank(f?.asset_codes ?? []),
        },
        {
            accessorKey: "mappedAsset",
            header: "Mapped Asset",
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.mappedAsset || "—"}</span>
            ),
            meta: multiWithBlank(f?.mapped_asset_codes ?? []),
        },
        {
            accessorKey: "investment",
            header: "Investment",
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.investment || "—"}</span>
            ),
            meta: multiWithBlank(f?.investment_codes ?? []),
        },
        {
            accessorKey: "mappedInvestment",
            header: "Mapped Investment",
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.mappedInvestment || "—"}</span>
            ),
            meta: multiWithBlank(f?.mapped_investment_codes ?? []),
        },
        {
            accessorKey: "sourceSystem",
            header: "Source System",
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.sourceSystem || "—"}</span>
            ),
            meta: multiWithBlank(f?.source_systems ?? []),
        },
        {
            accessorKey: "accountName",
            header: "Source Acc Name",
            cell: ({ row }) => (
                <div
                    className="max-w-[150px] truncate"
                    title={getDisplaySourceAccountName(
                        row.original.reference,
                        row.original.accountName,
                    )}
                >
                    {getDisplaySourceAccountName(row.original.reference, row.original.accountName)}
                </div>
            ),
            meta: text,
        },
        {
            accessorKey: "accountCode",
            header: "Source Acc No",
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.accountCode}</span>
            ),
            meta: text,
        },
        {
            accessorKey: "mappedAccountCode",
            header: "Acc Name",
            cell: ({ row }) => (
                <div
                    className="max-w-[150px] truncate"
                    title={row.original.mappedAccountCode ?? undefined}
                >
                    {row.original.mappedAccountCode ?? "—"}
                </div>
            ),
            meta: text,
        },
        {
            id: "mappedAccountNumber",
            accessorFn: (row) => row.mappedAccountCode ?? "",
            header: "Acc No",
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.mappedAccountCode ?? "—"}</span>
            ),
            meta: text,
        },
        {
            accessorKey: "amount",
            header: "Amount",
            cell: ({ row }) => {
                const amount = row.original.amount;
                if (!amount) return <span className="font-mono text-xs">—</span>;
                return (
                    <span className="font-mono text-xs text-right block">
                        {parseFloat(amount).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}
                    </span>
                );
            },
            meta: numberRange,
        },
        {
            accessorKey: "transactionDate",
            header: "Date",
            meta: date,
            cell: ({ row }) => (
                <span className="text-xs">{row.original.transactionDate ?? "—"}</span>
            ),
        },
        {
            accessorKey: "postingDate",
            header: "Post Month",
            meta: date,
            cell: ({ row }) => <span className="text-xs">{row.original.postingDate ?? "—"}</span>,
        },
        {
            accessorKey: "bookNumber",
            header: "Book Number",
            cell: ({ row }) => <span className="font-mono text-xs">{row.original.bookNumber}</span>,
            meta: multi(f?.book_numbers ?? []),
        },
        {
            accessorKey: "description",
            header: "Description",
            cell: ({ row }) => (
                <div
                    className="max-w-[150px] truncate"
                    title={row.original.description ?? undefined}
                >
                    {row.original.description ?? "—"}
                </div>
            ),
            meta: text,
        },
        {
            accessorKey: "reference",
            header: "Ref",
            cell: ({ row }) => {
                const ref = row.original.reference;
                const displayRef = getDisplayRef(ref);
                return (
                    <span
                        className={`font-mono text-xs ${
                            ref === "REBALANCE" ? "text-orange-600 font-medium" : ""
                        }`}
                    >
                        {displayRef}
                    </span>
                );
            },
            meta: text,
        },
    ];
}

/** The open file, as far as the summary above the table needs it. */
export type TGLFileDetails = {
    id: string;
    dealName: string | null;
    status: string;
    period: string;
    periodLabel: string | null;
};

export type TGeneratedGLTabUseCases = {
    loadFacets: (fileId: string) => Promise<void>;
    fetchLines: (fileId: string, query: TApiLineItemQuery, signal?: AbortSignal) => Promise<void>;
    useGLLinesState: () => TAsyncState<TGLLineItem[]>;
    useGLLinesMeta: () => { total: number; totalUnfiltered: number; pageSize: number };
    useFacets: () => TLineItemFacets | null;
};

export type TGeneratedGLTabProps = {
    /** The loaded file. The shell does not render this tab until it has one. */
    file: TGLFileDetails;
    useCases: TGeneratedGLTabUseCases;
};

const GL_PAGE_SIZE = 100;

/**
 * Generated GL tab — the GL movement lines generated from a TB-only file, shown
 * inside the review page's ETL files tab.
 */
export const createGeneratedGLTab = () => {
    return function GeneratedGLTab({ file, useCases }: TGeneratedGLTabProps): React.ReactElement {
        const { loadFacets, fetchLines, useGLLinesState, useGLLinesMeta, useFacets } = useCases;
        const glLinesState = useGLLinesState();
        const meta = useGLLinesMeta();
        const facets = useFacets();

        useEffect(() => {
            void loadFacets(file.id);
        }, [loadFacets, file.id]);

        const fetchData = useCallback(
            (query: TApiLineItemQuery, signal?: AbortSignal) => {
                void fetchLines(file.id, query, signal);
            },
            [fetchLines, file.id],
        );

        const toQuery = useCallback(
            ({
                columnFilters,
                sorting,
                page,
                pageSize,
            }: {
                columnFilters: ColumnFiltersState;
                sorting: SortingState;
                page: number;
                pageSize: number;
            }): TApiLineItemQuery => ({
                ...lineItemColumnFiltersToQuery(columnFilters, sorting),
                skip: (page - 1) * pageSize,
            }),
            [],
        );

        const { page, setPage, sorting, onSortingChange, columnFilters, onColumnFiltersChange } =
            useServerTable<TApiLineItemQuery>({
                pageSize: GL_PAGE_SIZE,
                toQuery,
                fetchData,
            });

        const glLines = glLinesState.data ?? EMPTY_GL_LINES;
        const columns = useMemo(() => createGLLinesColumns(facets), [facets]);

        return (
            <div data-testid="generated-gl-tab" className="space-y-4">
                <Card className="p-4">
                    <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                            <span className="font-medium">Deal: </span>
                            {file.dealName ?? "Unknown"}
                        </div>
                        <div>
                            <span className="font-medium">Period: </span>
                            {file.periodLabel ?? file.period}
                        </div>
                        <div>
                            <span className="font-medium">Status: </span>
                            {file.status}
                        </div>
                        <div>
                            <span className="font-medium">GL Lines: </span>
                            {meta.totalUnfiltered}
                        </div>
                    </div>
                </Card>

                {glLinesState.status === "rejected" ? (
                    <Card className="p-6">
                        <p className="text-destructive">
                            Error loading GL lines: {glLinesState.error?.message ?? "Unknown error"}
                        </p>
                    </Card>
                ) : (
                    <DataTable
                        columns={columns}
                        storageKey="gl-lines"
                        data={glLines}
                        loading={glLinesState.status === "pending" && glLinesState.data === null}
                        emptyMessage="No GL-generated lines found for this file."
                        manualFiltering
                        manualSorting
                        columnFilters={columnFilters}
                        onColumnFiltersChange={onColumnFiltersChange}
                        sortingState={sorting}
                        onSortingChange={onSortingChange}
                        enablePerColumnTextSearch={false}
                        maxHeight={820}
                        className="border rounded-lg"
                        rowNoun={{ one: "line item", many: "line items" }}
                        rowCount={buildRowCount({
                            total: meta.total,
                            totalUnfiltered: meta.totalUnfiltered,
                            hasActiveFilters: hasAnyLineItemColumnFilter(columnFilters),
                        })}
                        pagination={{
                            page,
                            pageSize: meta.pageSize || GL_PAGE_SIZE,
                            total: meta.total,
                            onPageChange: setPage,
                        }}
                    />
                )}
            </div>
        );
    };
};
